import * as z from 'zod';

import { WELCOME_BONUS_GAME_WEIGHTING_OPTIONS } from '../constants';
import { generateZodEnumIds } from '../utils';
import { nullableCoercedNumberInput, nullableNumberInput } from './helpers';

export const wageringFieldsSchema = z.object({
  minSelections: nullableCoercedNumberInput(),
  minOddsPerSelection: nullableCoercedNumberInput(),
  maxBetLimit: nullableNumberInput(),
  gameWeightingContributions: z.array(
    z.object({
      category: z.enum(generateZodEnumIds(WELCOME_BONUS_GAME_WEIGHTING_OPTIONS)),
      contributionPercentage: nullableNumberInput(),
    })
  ),
});

export type WageringFieldsFormData = z.infer<typeof wageringFieldsSchema>;

export const validateSportsbookWageringFields = (data: WageringFieldsFormData, ctx: z.RefinementCtx) => {
  if (data.minSelections == null) {
    ctx.addIssue({
      path: ['minSelections'],
      message: 'Min. selections number is required.',
      code: z.ZodIssueCode.custom,
    });
  } else if (!Number.isInteger(data.minSelections) || data.minSelections < 1 || data.minSelections > 1_000) {
    ctx.addIssue({
      path: ['minSelections'],
      message: 'Must be a whole number between 1 and 1,000',
      code: z.ZodIssueCode.custom,
    });
  }

  if (data.minOddsPerSelection == null) {
    ctx.addIssue({
      path: ['minOddsPerSelection'],
      message: 'Min. odds of each selection is required.',
      code: z.ZodIssueCode.custom,
    });
  } else if (data.minOddsPerSelection < 1 || data.minOddsPerSelection > 10_000_000) {
    ctx.addIssue({
      path: ['minOddsPerSelection'],
      message: 'Must be between 1 and 10,000,000',
      code: z.ZodIssueCode.custom,
    });
  }
};

export const validateCasinoWageringFields = (data: WageringFieldsFormData, ctx: z.RefinementCtx) => {
  if (data.maxBetLimit == null) {
    ctx.addIssue({
      path: ['maxBetLimit'],
      message: 'Max bet limit is required.',
      code: z.ZodIssueCode.custom,
    });
  } else if (data.maxBetLimit <= 0 || data.maxBetLimit > 10_000_000) {
    ctx.addIssue({
      path: ['maxBetLimit'],
      message: 'Must be greater than 0 and at most 10,000,000',
      code: z.ZodIssueCode.custom,
    });
  }

  if (data.gameWeightingContributions.length !== WELCOME_BONUS_GAME_WEIGHTING_OPTIONS.length) {
    ctx.addIssue({
      path: ['gameWeightingContributions'],
      message: 'All game weighting categories are required.',
      code: z.ZodIssueCode.custom,
    });
  }

  data.gameWeightingContributions.forEach((gameWeightingContribution, index) => {
    const { contributionPercentage } = gameWeightingContribution;

    if (contributionPercentage == null) {
      ctx.addIssue({
        path: ['gameWeightingContributions', index, 'contributionPercentage'],
        message: 'Contribution percentage is required.',
        code: z.ZodIssueCode.custom,
      });
    } else if (
      !Number.isInteger(contributionPercentage) ||
      contributionPercentage < 0 ||
      contributionPercentage > 100
    ) {
      ctx.addIssue({
        path: ['gameWeightingContributions', index, 'contributionPercentage'],
        message: 'Must be a whole number between 0 and 100',
        code: z.ZodIssueCode.custom,
      });
    }
  });
};

export const wageringConfigurationSchema = wageringFieldsSchema.superRefine((data, ctx) => {
  if (
    data.minSelections != null &&
    (!Number.isInteger(data.minSelections) || data.minSelections < 1 || data.minSelections > 1_000)
  ) {
    ctx.addIssue({
      path: ['minSelections'],
      message: 'Must be a whole number between 1 and 1,000',
      code: z.ZodIssueCode.custom,
    });
  }

  if (data.minOddsPerSelection != null && (data.minOddsPerSelection < 1 || data.minOddsPerSelection > 10_000_000)) {
    ctx.addIssue({
      path: ['minOddsPerSelection'],
      message: 'Must be between 1 and 10,000,000',
      code: z.ZodIssueCode.custom,
    });
  }

  if (data.maxBetLimit != null && (data.maxBetLimit <= 0 || data.maxBetLimit > 10_000_000)) {
    ctx.addIssue({
      path: ['maxBetLimit'],
      message: 'Must be greater than 0 and at most 10,000,000',
      code: z.ZodIssueCode.custom,
    });
  }

  data.gameWeightingContributions.forEach((gameWeightingContribution, index) => {
    const { contributionPercentage } = gameWeightingContribution;

    if (
      contributionPercentage != null &&
      (!Number.isInteger(contributionPercentage) || contributionPercentage < 0 || contributionPercentage > 100)
    ) {
      ctx.addIssue({
        path: ['gameWeightingContributions', index, 'contributionPercentage'],
        message: 'Must be a whole number between 0 and 100',
        code: z.ZodIssueCode.custom,
      });
    }
  });
});

export type WageringConfigurationFormData = z.infer<typeof wageringConfigurationSchema>;
